import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

const safeJsonParse = (item) => {
  try {
    const parsed = localStorage.getItem(item)
    return parsed ? JSON.parse(parsed) : null
  } catch (error) {
    console.error(`Error parsing ${item} from localStorage:`, error)
    localStorage.removeItem(item)
    return null
  }
}

// Student login
export const loginStudent = createAsyncThunk("auth/loginStudent", async (credentials, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/student/login`, credentials)
    const user = { ...response.data.student, role: "student" }
    localStorage.setItem("token", response.data.token)
    localStorage.setItem("user", JSON.stringify(user))
    return { user, token: response.data.token }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Login failed")
  }
})

// Admin login
export const loginAdmin = createAsyncThunk("auth/loginAdmin", async (credentials, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/admin/login`, credentials)
    const inferredRole = response?.data?.admin?.role || "admin"
    const user = { ...response.data.admin, role: inferredRole }
    localStorage.setItem("token", response.data.token)
    localStorage.setItem("user", JSON.stringify(user))
    return { user, token: response.data.token }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Login failed")
  }
})

export const loginSuperAdmin = createAsyncThunk("auth/loginSuperAdmin", async (credentials, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/super-admin/login`, credentials)
    const user = { ...response.data.admin, role: "super_admin" }
    localStorage.setItem("token", response.data.token)
    localStorage.setItem("user", JSON.stringify(user))
    return { user, token: response.data.token }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Super Admin login failed")
  }
})

// Faculty login
export const loginFaculty = createAsyncThunk("auth/loginFaculty", async (credentials, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/auth/faculty/login`, credentials)
    const fac = response.data.faculty
    const normalizedDesig = (fac?.designation || "").toLowerCase()
    const inferredRole = normalizedDesig.includes("hod") || normalizedDesig.includes("principal") ? "hod" : "faculty"
    const user = { ...fac, role: inferredRole }
    localStorage.setItem("token", response.data.token)
    localStorage.setItem("user", JSON.stringify(user))
    return { user, token: response.data.token }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Faculty login failed")
  }
})

// Student registration
export const registerStudent = createAsyncThunk("auth/registerStudent", async (studentData, { rejectWithValue }) => {
  try {
    const config = {
      headers:
        studentData instanceof FormData
          ? { "Content-Type": "multipart/form-data" }
          : { "Content-Type": "application/json" },
    }
    const response = await axios.post(`${API_URL}/auth/student/register`, studentData, config)
    const user = { ...response.data.student, role: "student" }
    localStorage.setItem("token", response.data.token)
    localStorage.setItem("user", JSON.stringify(user))
    return { user, token: response.data.token }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Registration failed")
  }
})

// Admin registration
export const registerAdmin = createAsyncThunk("auth/registerAdmin", async (adminData, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.post(`${API_URL}/auth/admin/register`, adminData, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const user = { ...response.data.admin, role: "admin" }
    localStorage.setItem("user", JSON.stringify(user))
    return { user }
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Admin registration failed")
  }
})

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: safeJsonParse("user"),
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      state.user = null
      state.token = null
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // student login
      .addCase(loginStudent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginStudent.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginStudent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // admin login
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      //super admin
      .addCase(loginSuperAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginSuperAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginSuperAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // faculty login
      .addCase(loginFaculty.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginFaculty.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(loginFaculty.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // student registration
      .addCase(registerStudent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerStudent.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
      })
      .addCase(registerStudent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // admin registration
      .addCase(registerAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
      })
      .addCase(registerAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { logout, clearError } = authSlice.actions
export default authSlice.reducer
